/**
 * The lead sentence of a refused Test Connection leg (Codex C2-02, merge C1+C2), worded ONLY from the structured
 * context main sends with the outcome (`StorageProbeContext`: the request, where it ran, what it needs, the remedies
 * this connection can use). Nothing here reads main's prose: main's sentence stays the leg's Details.
 *
 * - Entra, account listing refused → Reader or the data reader role on the storage account (+ the Single resource
 *   attach when main offers it).
 * - Entra, data read refused (a single resource, or the account's named share) → the service's DATA role at the item
 *   or the account — never Reader, which authorizes no data read (Microsoft Learn) — plus the account-key switch only
 *   when main offers it (the Data access field exists in "Storage account" mode only).
 * - SAS → the permission (and, for an account SAS, the resource type) the request needs, or the service it leaves out.
 * - Refusals no permission fixes (Codex merge-C C-M01), each with its own remedy: a SAS outside its allowed IP range,
 *   an HTTPS-only SAS over HTTP, Shared Key disallowed on the account, a firewall / network rule (`AuthorizationFailure`).
 */
import type { StorageProbeContext, StorageProbeRemedy, StorageProbeScope, StorageRoleScope, StorageService } from '@/types/azureStorage';

const SERVICE_NAMES: Record<StorageService, string> = { blob: 'Blob', table: 'Table', queue: 'Queue', file: 'File' };
const ITEM_NOUNS: Record<StorageService, { one: string; many: string }> = {
  blob: { one: 'container', many: 'containers' },
  table: { one: 'table', many: 'tables' },
  queue: { one: 'queue', many: 'queues' },
  file: { one: 'share', many: 'shares' },
};
const PERMISSION_NAMES = { read: 'Read', list: 'List' } as const;
const RESOURCE_TYPE_NAMES = { service: 'Service', container: 'Container', object: 'Object' } as const;

/** "this container" / "this folder" … — the item the request ran on; empty at account scope. */
const ITEM_HERE: Record<StorageProbeScope, string> = {
  account: '', container: 'this container', blob: 'this blob', table: 'this table', queue: 'this queue', share: 'this share', directory: 'this folder', file: 'this file',
};

/** What a refused data read would have read. `attached` = a single-resource connection (else: the account's named share). */
function dataReadOf(scope: StorageProbeScope, service: StorageService, attached: boolean): string {
  switch (scope) {
    case 'container': return 'blobs in this container';
    case 'blob': return 'this blob';
    case 'table': return 'entities in this table';
    case 'queue': return 'messages in this queue';
    case 'share': return attached ? 'files in this share' : 'files in the named share';
    case 'directory': return 'this folder';
    case 'file': return 'this file';
    case 'account': return `${SERVICE_NAMES[service].toLowerCase()} data`;
  }
}

/** Where a role can be assigned, relative to the item the request ran on ("its container" for a blob). */
function roleScopeWords(scope: StorageRoleScope, at: StorageProbeScope, attached: boolean): string {
  if (scope === 'account') return 'the storage account';
  if (scope === at) return scope === 'share' && !attached ? 'that share' : ITEM_HERE[at];
  return `its ${scope}`;
}

/** What a refused SAS request would have done. */
function sasActionOf(probe: StorageProbeContext, service: StorageService): string {
  switch (probe.operation) {
    case 'list-containers':
    case 'list-tables':
    case 'list-queues':
    case 'list-shares': return `list the account's ${ITEM_NOUNS[service].many}`;
    case 'service-properties': return `read the ${SERVICE_NAMES[service]} service's properties`;
    case 'list-blobs': return 'list the blobs in this container';
    case 'blob-properties': return 'read this blob';
    case 'query-entities': return "read this table's entities";
    case 'queue-metadata': return 'read this queue';
    case 'peek-messages': return "peek at this queue's messages";
    case 'list-directory': return probe.scope === 'share' ? 'list this share' : 'list this folder';
    case 'file-properties': return 'read this file';
  }
}

const find = <K extends StorageProbeRemedy['kind']>(remedies: StorageProbeRemedy[], kind: K): Extract<StorageProbeRemedy, { kind: K }> | undefined =>
  remedies.find((remedy): remedy is Extract<StorageProbeRemedy, { kind: K }> => remedy.kind === kind);

function useSasSentence(remedy: Extract<StorageProbeRemedy, { kind: 'use-sas' }>, at: StorageProbeScope): string {
  if (!remedy.permission) return '';
  const permission = PERMISSION_NAMES[remedy.permission];
  if (remedy.resourceType) return `an account SAS with the ${permission} permission and the ${RESOURCE_TYPE_NAMES[remedy.resourceType]} resource type`;
  return `a SAS with the ${permission} permission${ITEM_HERE[at] ? ` on ${ITEM_HERE[at]}` : ''}`;
}

/** The lead for a refused leg, or `undefined` when main classified no requirement (the leg keeps main's sentence). */
export function failedLegLead(service: StorageService, probe: StorageProbeContext, reportScope: 'account' | 'resource' | 'emulator'): string | undefined {
  const requirement = probe.requirement;
  if (!requirement) return undefined;
  const attached = reportScope === 'resource';
  const { remedies } = probe;
  const name = SERVICE_NAMES[service];
  switch (requirement.kind) {
    case 'entra-list': {
      const assign = find(remedies, 'assign-role');
      const attach = find(remedies, 'attach-single-resource');
      const roles = (assign?.roles ?? requirement.roles).join(' or ');
      const attachClause = attach ? `, or choose Single resource under Connect using above and paste one ${attach.resource}'s URL` : '';
      return `No role lets this identity list the account's ${ITEM_NOUNS[service].many}. Assign ${roles} on the storage account${attachClause}.`;
    }
    case 'entra-data': {
      const assign = find(remedies, 'assign-role');
      const where = (assign?.scopes ?? requirement.scopes).map((scope) => roleScopeWords(scope, probe.scope, attached)).join(' or ');
      const keyClause = find(remedies, 'use-account-key') ? ', or switch Data access above to the account key' : '';
      return `This identity can't read ${dataReadOf(probe.scope, service, attached)}. Assign ${assign?.roles[0] ?? requirement.role} on ${where}${keyClause}.`;
    }
    case 'sas-permission': {
      const useSas = find(remedies, 'use-sas');
      const policy = find(remedies, 'amend-policy');
      const other = useSas ? useSasSentence(useSas, probe.scope) : '';
      const fix = policy
        ? `Add ${PERMISSION_NAMES[policy.permission]} to the stored access policy behind it${other ? `, or use ${other}` : ''}.`
        : other ? `Use ${other}.` : '';
      return `This SAS can't ${sasActionOf(probe, service)}.${fix ? ` ${fix}` : ''}`;
    }
    case 'sas-service': {
      if (requirement.sas === 'service') return `This SAS was issued for another service. Use a SAS issued for the ${name} service.`;
      const untick = find(remedies, 'untick-service') ? `, or untick ${name} under Services above` : '';
      return `This SAS doesn't cover the ${name} service. Use a SAS that includes it${untick}.`;
    }
    case 'sas-source-ip':
      // Z5: AuthorizationFailure does not say which rule refused the address — the SAS's range or the account's network.
      if (requirement.evidence === 'authorization-failure') {
        return "Azure refused this request from this computer (AuthorizationFailure). This SAS allows requests only from its IP range, and the storage account's network rules can refuse an address too. Check both: that the SAS's allowed IP range includes this computer's public IP address (or use a SAS without an IP range), and that Networking in the Azure portal allows this computer's address.";
      }
      return "This SAS allows requests only from its IP range, and Azure refused this computer's address. Use a SAS whose allowed IP range includes this computer's public IP address, or one without an IP range."
        + (find(remedies, 'allow-network') ? " If the storage account's firewall limits access too, allow the address under Networking in the Azure portal." : '');
    case 'sas-protocol':
      return `This SAS allows HTTPS only, and this connection sends its requests over HTTP. Use an https:// endpoint${find(remedies, 'sas-allow-http') ? ', or a SAS that allows HTTP as well' : ''}.`;
    case 'shared-key-disabled': {
      const entra = find(remedies, 'use-entra');
      const signIn = entra?.via === 'data-access' ? 'Switch Data access above to Microsoft Entra data roles' : 'Choose a Microsoft Entra sign-in under Authentication above';
      const delegation = find(remedies, 'use-user-delegation-sas') ? ', or use a user delegation SAS (signed with Microsoft Entra credentials)' : '';
      const refused = requirement.signedWith === 'sas'
        ? 'it refuses a SAS signed with an account key, whatever the SAS grants'
        : entra?.via === 'data-access' ? 'it refuses the account key this identity read' : 'it refuses the account key';
      return `This storage account doesn't allow Shared Key authorization, so ${refused}. ${signIn}${delegation}.`;
    }
    case 'network-rules':
      return "Azure refused this request without naming a missing permission (AuthorizationFailure): the storage account's firewall or network rules, or disabled public network access, may block this computer's address. Allow it under Networking in the Azure portal."
        + (find(remedies, 'check-role-condition') ? " If the network is allowed, check for a condition on this identity's role assignment." : '');
  }
}
